/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Info, Monitor } from 'lucide-react';
import { api, type FeatureFlag } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  OFFICE_FLAG_GROUP_TITLES,
  OFFICE_FLAG_ROLES_NOTE,
  OFFICE_FLAG_SPECS,
  officeAppReadFailure,
  officeAppSummary,
  officeFlagPatch,
  officeFlagState,
  type OfficeAppRead,
  type OfficeFlagGroup,
  type OfficeFlagSpec,
} from '../lib/governance';
import { Callout, Icon, NumberInput, Panel, Skeleton, Switch, Table, Tag, type Column } from '../ui';
import { SaveNote } from './SaveNote';

const GROUPS: OfficeFlagGroup[] = ['hosts', 'edits', 'reach'];

export interface OfficeCardProps {
  /**
   * The stored feature flags. Governance keeps the one copy the generic Feature flags table also uses, so a save in
   * either place shows in both and neither sends a stale value.
   */
  flags: Record<string, FeatureFlag>;
  /** Why the flags could not be read; the card says so instead of showing defaults as if they were stored. */
  flagsError: string | null;
  /** A save's answer: the service's whole flag map, kept by Governance for both views. */
  onFlagsSaved: (flags: Record<string, FeatureFlag>) => void;
}

/**
 * Governance → Office: the office app's central policy (read from Apps & Surfaces) and the switches that decide who gets
 * Gyro in each Office app, automatic low-risk edits and Microsoft 365 reach. The switches are the generic feature flags
 * (PUT /v1/admin/flags/:key); every save is audited by the service (admin.flag_changed).
 */
export function OfficeCard({ flags, flagsError, onFlagsSaved }: OfficeCardProps) {
  // Loading, failed or answered: a failed read is never mistaken for "this service has no Office add-in".
  const [appRead, setAppRead] = useState<OfficeAppRead>({ status: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [percentDrafts, setPercentDrafts] = useState<Record<string, string>>({});
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    api.apps().then(
      (result) => {
        if (live) setAppRead({ status: 'loaded', app: (result.apps ?? []).find((entry) => entry.key === 'office') });
      },
      (error: unknown) => {
        if (live) setAppRead(officeAppReadFailure(error));
      },
    );
    return () => {
      live = false;
    };
  }, []);

  const save = async (spec: OfficeFlagSpec, change: { on?: boolean; percent?: number }) => {
    if (flagsError) return;
    const current = officeFlagState(spec, flags[spec.key]);
    const patch = officeFlagPatch(current, change);
    if (patch.enabled === current.on && patch.percent === current.percent && current.stored) return;
    setBusy(spec.key);
    setNote(null);
    try {
      const saved = await api.setFlag(spec.key, patch);
      onFlagsSaved(saved.flags);
      setNote({ ok: true, text: `${spec.title}: ${officeFlagState(spec, saved.flags[spec.key]).summary}` });
    } catch (error) {
      setNote({ ok: false, text: `${spec.title}: ${errorText(error, 'the switch was not saved.')}` });
    } finally {
      setBusy(null);
    }
  };

  /** Save a typed percentage once (blur or Enter); an empty or non-numeric entry is dropped, never saved as 0. */
  const commitPercent = (spec: OfficeFlagSpec) => {
    const draft = percentDrafts[spec.key];
    if (draft === undefined) return;
    setPercentDrafts((drafts) => {
      const next = { ...drafts };
      delete next[spec.key];
      return next;
    });
    const value = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(value)) return;
    void save(spec, { percent: value });
  };

  const summary = officeAppSummary(appRead);
  const summaryTone = summary.state === 'absent' ? 'warn' : summary.state === 'unknown' ? 'neutral' : 'info';
  const rows = GROUPS.flatMap((group) => OFFICE_FLAG_SPECS.filter((spec) => spec.group === group));
  const columns: Array<Column<OfficeFlagSpec>> = [
    {
      key: 'switch', header: 'Switch',
      cell: (spec) => {
        const state = officeFlagState(spec, flags[spec.key]);
        return (
          <div className="ui-cell-stack">
            <span className="ui-cell-title"><strong id={`office-flag-${spec.key}-title`}>{spec.title}</strong> <Tag>{spec.key}</Tag></span>
            <span className="ui-cell-sub">{spec.description}</span>
            <span className="ui-cell-sub" aria-live="polite">{state.summary}</span>
          </div>
        );
      },
    },
    {
      key: 'state', header: 'State', width: '120px',
      cell: (spec) => {
        const state = officeFlagState(spec, flags[spec.key]);
        return <Switch checked={state.on} disabled={busy === spec.key} onChange={(on) => void save(spec, { on })} label={spec.title} labelHidden stateLabels={['on', 'off']} />;
      },
    },
    {
      key: 'percent', header: 'Percent', align: 'end', width: '140px',
      cell: (spec) => {
        const state = officeFlagState(spec, flags[spec.key]);
        const percentText = percentDrafts[spec.key] ?? String(state.percent);
        return (
          <NumberInput
            size="sm"
            type="number"
            min={0}
            max={100}
            step={1}
            suffix="%"
            className="ui-pct"
            aria-label={`${spec.title}: percent of people`}
            title="Percent of people who get it (0 to 100). Saved when you leave the field or press Enter."
            value={percentText}
            disabled={busy === spec.key || !state.on}
            onChange={(e) => setPercentDrafts((drafts) => ({ ...drafts, [spec.key]: e.target.value }))}
            onBlur={() => commitPercent(spec)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitPercent(spec);
            }}
          />
        );
      },
    },
  ];

  return (
    <Panel
      flush
      title={<span className="ui-title-icon"><Icon icon={Monitor} size="md" />Office add-in</span>}
      description="Who gets Gyro in each Office app, automatic low-risk edits and Microsoft 365 reach"
      footer={<><Icon icon={Info} size="sm" /><span>{OFFICE_FLAG_ROLES_NOTE} Microsoft 365 reach stays off until IT has granted admin consent; switch it on afterwards.</span></>}
    >
      <div className="ui-panel-pad ui-panel-pad--bottom">
        {summary.state === 'loading' && <><span className="ui-sr">Reading whether the Office add-in is switched on</span><Skeleton size="sm" width="240px" /></>}
        {summary.lines.length > 0 && (
          <Callout tone={summaryTone}>
            {summary.lines.map((line) => <p key={line} className="ui-para">{line}</p>)}
          </Callout>
        )}
      </div>
      {flagsError ? (
        <div className="ui-panel-pad"><Callout tone="bad" role="alert">{flagsError}</Callout></div>
      ) : (
        <Table stacked
          label="Office add-in switches"
          columns={columns}
          rows={rows}
          rowKey={(spec) => spec.key}
          groupOf={(spec) => OFFICE_FLAG_GROUP_TITLES[spec.group]}
          stickyHeader={false}
          className="ui-table--tall"
        />
      )}
      <div className="ui-panel-pad ui-panel-pad--top ui-panel-pad--note"><SaveNote note={note} /></div>
    </Panel>
  );
}
